import { Outlet, useOutletContext } from 'react-router-dom'
import { useState } from 'react'
import { ThemeProvider } from 'next-themes'

import { AegisSidebar } from '@/components/aegis-sidebar'
import { PanelContainerContext } from '@/components/detail-panel'
import { SiteHeader } from '@/components/site-header'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { usePermissions } from '@/lib/permissions'

export type AppOutletContext = { can: (resource: string, action: string) => boolean }
export function useAppContext() {
  return useOutletContext<AppOutletContext>()
}

export default function AppLayout({
  walletTotal,
  overdueCount,
  upcomingCount,
}: {
  walletTotal?: number
  overdueCount?: number
  upcomingCount?: number
}) {
  const [panelContainer, setPanelContainer] = useState<HTMLDivElement | null>(null)
  const { can } = usePermissions()

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <SidebarProvider
        style={
          {
            '--sidebar-width': 'calc(var(--spacing) * 64)',
            '--header-height': 'calc(var(--spacing) * 12)',
          } as React.CSSProperties
        }
      >
        <AegisSidebar
          variant="inset"
          overdueCount={overdueCount ?? 0}
          upcomingCount={upcomingCount ?? 0}
        />
        <SidebarInset>
          <SiteHeader walletTotal={walletTotal} />
          <div className="flex flex-1 flex-col overflow-hidden relative">
            <div className="@container/main flex flex-1 flex-col gap-2 overflow-auto">
              <PanelContainerContext.Provider value={panelContainer}>
                <Outlet context={{ can } satisfies AppOutletContext} />
              </PanelContainerContext.Provider>
            </div>
            {/* Portal target for DetailPanel — absolute, fills content area, above scroll */}
            <div
              ref={(el) => { if (el && el !== panelContainer) setPanelContainer(el) }}
              className="absolute inset-0 pointer-events-none z-40"
            />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ThemeProvider>
  )
}
